import { deskToolHeadline } from "./deskToolWords.ts";
import type { ApprovalCall } from "@cleo/contracts";

// W-dc12e842: the structured identity of what Approve lets run (@cleo/contracts ApprovalCall), as the Desk's adapter
// recorded it beside the request, and the words and text every approval card (the Desk's conversation card and Inbox,
// Pocket's approval card) shows for it. Nothing here reads the request's text for identity, and nothing here trims,
// shortens or normalises what will run: a card shows exactly what Approve releases (render it through
// requestText.ts so hidden characters show as markers).

const APPROVAL_CALL_KINDS: ReadonlySet<string> = new Set([
  "tool",
  "command",
  "file-change",
  "file-read",
  "permission",
]);
const CHANGE_KINDS: ReadonlySet<string> = new Set(["add", "delete", "update"]);
const optionalString = (record: Record<string, unknown>, key: string): boolean =>
  record[key] === undefined || typeof record[key] === "string";
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * A stored or received call, checked field by field (@cleo/contracts ApprovalCall); anything else is no call, and
 * the card stays general. `deskOwnServer` must be exactly true to mark Cleo's own.
 */
export function approvalCallOf(value: unknown): ApprovalCall | null {
  if (!isRecord(value)) return null;
  if (typeof value["kind"] !== "string" || !APPROVAL_CALL_KINDS.has(value["kind"])) return null;
  if (typeof value["deskOwnServer"] !== "boolean") return null;
  if (!["server", "tool", "command", "reason"].every((key) => optionalString(value, key)))
    return null;
  const paths = value["paths"];
  if (
    paths !== undefined &&
    !(Array.isArray(paths) && paths.every((path) => typeof path === "string"))
  )
    return null;
  const preview = value["preview"];
  if (
    preview !== undefined &&
    !(
      isRecord(preview) &&
      typeof preview["title"] === "string" &&
      Array.isArray(preview["lines"]) &&
      preview["lines"].every((line) => typeof line === "string")
    )
  )
    return null;
  const network = value["network"];
  if (
    network !== undefined &&
    !(
      isRecord(network) &&
      typeof network["host"] === "string" &&
      typeof network["protocol"] === "string" &&
      (network["port"] === undefined || typeof network["port"] === "number")
    )
  )
    return null;
  const changes = value["changes"];
  if (
    changes !== undefined &&
    !(
      Array.isArray(changes) &&
      changes.every(
        (change) =>
          isRecord(change) &&
          typeof change["path"] === "string" &&
          typeof change["kind"] === "string" &&
          CHANGE_KINDS.has(change["kind"]) &&
          ["movePath", "diff", "content"].every((key) => optionalString(change, key)),
      )
    )
  )
    return null;
  return value as ApprovalCall;
}

/** The Desk's own tool call a request is (its adapter established the Desk's own server), or null. */
export function isDeskOwnToolCall(call: ApprovalCall | null | undefined): boolean {
  return (
    call?.kind === "tool" &&
    call.deskOwnServer === true &&
    typeof call.tool === "string" &&
    call.tool !== ""
  );
}

/** Each file a change approval releases, in full: its path, what happens to it, and its diff or content. */
export function fileChangesText(changes: NonNullable<ApprovalCall["changes"]>): string {
  return changes
    .map((change) => {
      const head =
        change.kind === "add"
          ? `Add ${change.path}`
          : change.kind === "delete"
            ? `Delete ${change.path}`
            : change.movePath !== undefined
              ? `Update ${change.path} and move it to ${change.movePath}`
              : `Update ${change.path}`;
      const body = change.diff ?? change.content;
      return body === undefined ? head : `${head}\n${body}`;
    })
    .join("\n\n");
}

const argumentsText = (args: unknown): string | null =>
  args === undefined ? null : JSON.stringify(args, null, 2);

/**
 * What a call will run, for a card that does not describe it in plain words: a headline by its kind, and the code
 * that runs or is granted (the command exactly, each file's changes, the access asked for, a tool's arguments), whole.
 * Another server's name is quoted as a name, never as the subject, so a server named "Cleo" cannot read as Cleo.
 */
export function approvalCallWillRun(call: ApprovalCall): {
  readonly headline: string;
  readonly code: string | null;
  readonly reason: string | null;
} {
  const reason = call.reason !== undefined && call.reason.trim() !== "" ? call.reason : null;
  const paths = call.paths && call.paths.length > 0 ? `: ${call.paths.join(", ")}` : "";
  switch (call.kind) {
    case "command":
      return {
        headline:
          call.network !== undefined
            ? `Let Cleo's engine reach ${call.network.host} (${call.network.protocol}${call.network.port !== undefined ? `, port ${call.network.port}` : ""})`
            : "Run a command on your Mac",
        code: call.command !== undefined && call.command !== "" ? call.command : null,
        reason,
      };
    case "file-change": {
      const changed =
        call.changes && call.changes.length > 0
          ? `: ${call.changes.map((change) => change.path).join(", ")}`
          : paths;
      return {
        headline: `Change files on your Mac${changed}`,
        code:
          call.changes && call.changes.length > 0
            ? fileChangesText(call.changes)
            : argumentsText(call.args),
        reason,
      };
    }
    case "file-read":
      return { headline: `Read files on your Mac${paths}`, code: argumentsText(call.args), reason };
    case "permission":
      return {
        headline: `Give Cleo's engine more access${call.tool?.trim() ? `: ${call.tool}` : ""}`,
        code: argumentsText(call.args),
        reason,
      };
    case "tool": {
      const tool = call.tool?.trim() ? call.tool : "a tool";
      return {
        headline: isDeskOwnToolCall(call)
          ? (call.preview?.title ?? deskToolHeadline(tool, call.args) ?? `Let Cleo use ${tool}`)
          : call.server?.trim()
            ? `Use a tool from another server (${call.server}): ${tool}`
            : `Use a tool that is not one of Cleo's: ${tool}`,
        code: argumentsText(call.args),
        reason,
      };
    }
  }
}

/**
 * The complete request, from the call itself: what runs (the command, the tool and its server, each file's changes,
 * the access asked for), every argument whole, and the engine's reason. Shown under "Show request" and "The exact
 * request"; never shortened, so a long value is there in full (the view scrolls).
 */
export function approvalCallRequestText(call: ApprovalCall): string {
  const sections: string[] = [];
  if (call.network !== undefined)
    sections.push(
      `Network: ${call.network.host} (${call.network.protocol}${call.network.port !== undefined ? `, port ${call.network.port}` : ""})`,
    );
  if (call.kind === "command" && call.command !== undefined) sections.push(call.command);
  if (call.kind === "tool")
    sections.push(
      `Tool: ${call.tool ?? "(not named)"}${call.server !== undefined ? `\nServer: ${call.server}` : ""}`,
    );
  if (call.kind === "permission" && call.tool !== undefined) sections.push(`Access: ${call.tool}`);
  if (call.changes && call.changes.length > 0) sections.push(fileChangesText(call.changes));
  else if (call.paths && call.paths.length > 0) sections.push(`Files:\n${call.paths.join("\n")}`);
  if (call.args !== undefined) sections.push(`Arguments:\n${JSON.stringify(call.args, null, 2)}`);
  if (call.reason !== undefined && call.reason !== "") sections.push(`Reason:\n${call.reason}`);
  return sections.join("\n\n");
}

/**
 * W-dc12e842 (fail closed): the one rule every approval card follows, on the Desk and in Pocket. A card that cannot
 * show the complete request for its kind offers no Approve, only Decline: a request with no structured call (an older
 * record, or one recorded on another device), a command with neither its command nor the host it asks to reach, a
 * file change carrying neither its changes nor any argument, a read naming no file, more access or a tool call
 * without its arguments.
 */
export function approvalCallIncomplete(call: ApprovalCall | null | undefined): boolean {
  if (call === null || call === undefined) return true;
  const hasArgs =
    call.args !== undefined && !(isRecord(call.args) && Object.keys(call.args).length === 0);
  switch (call.kind) {
    case "command":
      return (call.command === undefined || call.command === "") && call.network === undefined;
    case "file-change":
      return (call.changes === undefined || call.changes.length === 0) && !hasArgs;
    case "file-read":
      return (call.paths === undefined || call.paths.length === 0) && !hasArgs;
    case "permission":
    case "tool":
      return call.args === undefined;
  }
}

export const REQUEST_NOT_SHOWN =
  "Cleo can't show this request in full, so it can't be approved here.";
